import { keepPreviousData, useQuery } from '@tanstack/react-query';

export type PostInsights = {
  isLoading: boolean;
  likes: PostLike[];
  comment_count: number;
};

export type PostLike = {
  created_at: string;
  id: string;
  liked: boolean;
  post_id: string;
  subscription_id: number;
  updated_at: string;
  user_id: string | null;
};

type PostInsightsResponse = {
  likes: PostLike[];
  comment_count: number;
};

export const postInsightsKeys = {
  all: ['post-insights'] as const,
  byPost: (postId: string) => ['post-insights', postId] as const,
};

const getFilteredLikes = (likes: PostLike[]) => {
  if (!likes) return [];
  return likes.filter((like) => !!(like?.subscription_id || like?.user_id));
};

const fetchPostInsights = async (postId: string): Promise<PostInsightsResponse[]> => {
  const response = await fetch(`/post_insights?post_id=${postId}`);
  if (!response.ok) throw new Error('Failed to fetch post insights');
  return response.json();
};

const usePostInsights = (postId: string): PostInsights => {
  // Keyed by postId so navigating between posts in the SPA fetches the correct
  // insights instead of reusing the previous post's cached data.
  const { data, isLoading } = useQuery({
    queryKey: postInsightsKeys.byPost(postId),
    queryFn: () => fetchPostInsights(postId),
    enabled: !!postId,
    staleTime: 30_000,
    // Keep the previous post's insights visible while the next post's load is in
    // flight so the like/comment counts don't flash to zero during navigation.
    placeholderData: keepPreviousData,
  });

  const insights = data?.[0];

  return {
    isLoading,
    likes: getFilteredLikes(insights?.likes ?? []),
    comment_count: insights?.comment_count ?? 0,
  };
};

export default usePostInsights;
